import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle, ShieldCheck, Clock, Truck, Factory, ArrowRight } from "lucide-react";
import Layout from "../Layout";
import SEOHead from "../SEOHead";
import Breadcrumb from "../Breadcrumb";
import FAQSection from "../FAQSection";
import SectionHeader from "../SectionHeader";
import CTABand from "../CTABand";
import HeroSection from "../Home/HeroSection";
import { getIndustryBySlug, industries } from "../../data/industries";
import { business } from "../../config/business";

import heroImg from "../assets/industrial-structure.jpg";
import requirementsImg from "../assets/fabrication-hero.jpg";

const relevantServices = [
  { name: "PEB Buildings", path: "/pre-fab-structure-buildings" },
  { name: "Industrial Sheds", path: "/industrial-sheds" },
  { name: "Steel Fabrication", path: "/structure-steel-fabrication" },
  { name: "Design & Engineering", path: "/design-engineering" },
];

const IndustryPage = () => {
  const { slug } = useParams();
  const industry = getIndustryBySlug(slug);

  if (!industry) return <Navigate to="/industries" replace />;

  const otherIndustries = industries.filter((i) => i.slug !== industry.slug);

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: `PEB structures for ${industry.name}`,
    provider: { "@type": "Organization", name: business.name },
    description: industry.intro,
  };

  return (
    <Layout>
      <SEOHead
        title={`PEB Structures for ${industry.name} | TR Structures`}
        description={`${industry.tagline} TR Structures designs and fabricates PEB buildings for ${industry.name.toLowerCase()} across Telangana and Andhra Pradesh.`}
        keywords={`PEB for ${industry.name}, industrial shed ${industry.name}, ${industry.name} structure manufacturer, ${industry.name} warehouse construction, ${industry.name} steel building cost, PEB shed for ${industry.name} industry, ${industry.name} shed contractor Hyderabad`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceJsonLd)}</script>
      </Helmet>
      <Breadcrumb items={[{ name: "Industries", path: "/industries" }, { name: industry.name }]} />

      {/* 1. Hero */}
      <HeroSection
        title={
          <>
            PEB Structures for <span className="text-primary">{industry.name}</span>
          </>
        }
        subtitle={industry.tagline}
        description={industry.intro}
        backgroundImage={heroImg}
        primaryButtonText="Get Project Estimate"
        primaryButtonLink="/enquiry"
        secondaryButtonText="Contact Us"
        secondaryButtonLink="/contact"
      />

      {/* 2. Trust strip */}
      <section className="bg-primary py-6">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-primary-foreground text-center">
            <span className="font-montserrat font-semibold flex items-center gap-2"><ShieldCheck size={18} aria-hidden="true" /> Requirement-specific design</span>
            <span className="font-montserrat font-semibold flex items-center gap-2"><Clock size={18} aria-hidden="true" /> Fast fabrication & erection</span>
            <span className="font-montserrat font-semibold flex items-center gap-2"><Truck size={18} aria-hidden="true" /> Telangana & AP delivery</span>
          </div>
        </div>
      </section>

      {/* 3. Requirements — image/checklist split */}
      <section className="section-padding">
        <div className="container-custom grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="relative order-2 lg:order-1">
            <img
              src={requirementsImg}
              alt={`${industry.name} industrial structure - TR Structures`}
              className="rounded-2xl shadow-xl w-full h-[340px] md:h-[420px] object-cover"
              loading="lazy"
            />
            <div className="hidden sm:flex absolute -bottom-6 -right-6 bg-primary text-white rounded-2xl shadow-xl p-6 flex-col items-center justify-center text-center">
              <span className="font-montserrat text-3xl font-extrabold leading-none">{industry.requirements.length}</span>
              <span className="text-xs font-semibold mt-1 leading-snug">Key Design
                <br />Requirements
              </span>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <SectionHeader
              eyebrow="What This Sector Needs"
              title={`Structures Built for ${industry.name}`}
            />
            <div className="divide-y divide-border border-t border-b border-border">
              {industry.requirements.map((req) => (
                <div key={req.title} className="flex gap-4 py-5">
                  <CheckCircle className="text-secondary flex-shrink-0 mt-1" size={22} aria-hidden="true" />
                  <div>
                    <h3 className="font-montserrat font-bold text-foreground mb-1">{req.title}</h3>
                    <p className="text-sm text-muted-foreground">{req.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 4. Related services */}
      <section className="section-padding bg-muted">
        <div className="container-custom">
          <SectionHeader eyebrow="Where To Start" title="Relevant Services" align="center" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {relevantServices.map((service) => (
              <Link
                key={service.path}
                to={service.path}
                className="p-4 bg-card rounded-lg text-center font-montserrat font-semibold text-foreground hover:bg-primary hover:text-primary-foreground transition shadow-sm border border-border"
              >
                {service.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Other industries — scrollable pill row */}
      <section className="section-padding">
        <div className="container-custom">
          <SectionHeader eyebrow="Sectors We Serve" title="Other Industries We Serve" align="center" />
          <div className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 snap-x">
            {otherIndustries.map((ind) => (
              <Link
                key={ind.slug}
                to={`/industries/${ind.slug}`}
                className="group shrink-0 snap-start flex items-center gap-3 bg-card border border-border rounded-full px-6 py-4 shadow-sm card-hover"
              >
                <Factory className="text-primary" size={20} aria-hidden="true" />
                <span className="font-montserrat font-semibold text-foreground whitespace-nowrap">
                  {ind.name}
                </span>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/industries" className="btn-outline inline-flex items-center gap-2 px-8 py-3">
              View All Industries <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <FAQSection faqs={industry.faqs} title={`${industry.name} — Frequently Asked Questions`} />

      {/* 7. CTA */}
      <CTABand
        title={`Discuss Your ${industry.name} Project`}
        description="Share your requirements and our engineering team will work through the right design approach."
        primaryText="Get Project Estimate"
        primaryLink="/enquiry"
      />
    </Layout>
  );
};

export default IndustryPage;
